import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis,
} from 'recharts';
import { CHART_GROW_ANIMATION } from '../chartAnimation';
import { ClrBarHover } from '../chartMarkHover';
import ClrRechartsTooltip from '../ClrRechartsTooltip';
import { buildNiceAxisScale, CLR_VALUE_Y_AXIS_WIDTH, getSeriesMax } from '../axisScale';

const BUCKET_ORDER = ['0-7d', '8-14d', '15-30d', '31-60d', '60+d'];
const BAR_COLOR = '#26735C';
const BAR_SIZE = 50.7687873840332;

function ClosureTimeDistributionCard({ data }) {
  const chartData = useMemo(() => {
    const map = (data || []).reduce((acc, row) => {
      const key = row.bucket || row.bucket_label;
      acc[key] = row.count ?? row.referral_count ?? 0;
      return acc;
    }, {});
    return BUCKET_ORDER.map((bucket) => ({
      bucket,
      count: map[bucket] || 0,
    }));
  }, [data]);

  const axis = useMemo(() => {
    const maxCount = getSeriesMax(chartData, ['count']);
    return buildNiceAxisScale(maxCount);
  }, [chartData]);

  return (
    <div className="clr-chart-card">
      <div className="clr-chart-card-header clr-chart-card-header-accent">
        <h3 className="clr-chart-card-title">Closure time distribution</h3>
      </div>
      <div className="clr-chart-card-body">
        {(data || []).length === 0 ? (
          <p className="clr-chart-plot clr-chart-empty">No closure data</p>
        ) : (
        <div className="clr-chart-plot">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="0" vertical={false} stroke="#DBDEE4" />
              <XAxis
                dataKey="bucket"
                height={24}
                tickMargin={4}
                tick={{ fontSize: 11, fill: '#6b7a74' }}
                axisLine={{ stroke: '#e0e4e0' }}
                tickLine={false}
              />
              <YAxis
                width={CLR_VALUE_Y_AXIS_WIDTH}
                tick={{ fontSize: 11, fill: '#6b7a74' }}
                axisLine={false}
                tickLine={false}
                domain={[0, axis.domainMax]}
                ticks={axis.ticks}
              />
              <ClrRechartsTooltip
                cursor={{ fill: 'rgba(62, 79, 76, 0.06)' }}
                formatter={(payload) => ({
                  title: null,
                  minWidth: BAR_SIZE,
                  rows: [{
                    key: 'count',
                    value: payload[0]?.value ?? 0,
                  }],
                })}
              />
              <Bar
                dataKey="count"
                fill={BAR_COLOR}
                activeBar={ClrBarHover}
                radius={[0, 0, 0, 0]}
                barSize={BAR_SIZE}
                maxBarSize={BAR_SIZE}
                {...CHART_GROW_ANIMATION}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        )}
      </div>
    </div>
  );
}

ClosureTimeDistributionCard.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    bucket: PropTypes.string,
    bucket_label: PropTypes.string,
    count: PropTypes.number,
    referral_count: PropTypes.number,
  })),
};

export default ClosureTimeDistributionCard;
